import { ApiError, okResponse, type RefusalOptions, refusalError } from '@crow/service-kit/web/api';
import { isPreconditionReason, type PreconditionReason } from '@crowdex/agent-contracts';
import type { ApiErrorCode, ApiResult } from './http-types';

type Issues = ReadonlyArray<{ readonly path: string; readonly message: string }>;

/** The kit's `ApiError`, with what crowdex's envelope adds: the invalid fields and what is missing. */
export class ApiClientError extends ApiError<ApiErrorCode> {
  override readonly issues: Issues;
  /** What is missing, when the failure is one the user can fix (no agent, folder gone…). */
  readonly reason: PreconditionReason | null;

  constructor(
    message: string,
    status: number,
    code: ApiErrorCode | null = null,
    issues: Issues = [],
    reason: PreconditionReason | null = null,
    body?: unknown,
  ) {
    super(status, code, message, body);
    this.name = 'ApiClientError';
    this.issues = issues;
    this.reason = reason;
  }
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

const API_ERROR_CODES: ReadonlySet<string> = new Set<ApiErrorCode>([
  'unauthorized',
  'invalid_request',
  'not_found',
  'conflict',
  'unconfigured',
  'unavailable',
  'internal_error',
]);

function withFirstIssue(message: string, issues: Issues): string {
  const first = issues[0];
  if (first === undefined) return message;
  return `${message} ${first.path ? `${first.path}: ` : ''}${first.message}`;
}

/**
 * How crowdex reads a refusal, through the kit's `readApiError`: its `ApiClientError`, from the
 * services' `{ error: { code, message, issues } }`, crowdex's former `ApiResult` envelope
 * (`{ ok: false, error: { code, message, reason } }`), a bare `{ error: "…" }`, the server's own
 * failures' `{ statusCode, error, message }`, or plain text. Never the raw JSON.
 */
export const apiRefusals: RefusalOptions = {
  fallbackMessage: (status) => `Request failed (HTTP ${status}).`,
  createError(status, code, message, body) {
    const envelope = record(record(body)?.['error']);
    if (envelope === null) return new ApiClientError(message, status, null, [], null, body);
    const issues = Array.isArray(envelope['issues']) ? (envelope['issues'] as Issues) : [];
    return new ApiClientError(
      withFirstIssue(message, issues),
      status,
      code !== null && API_ERROR_CODES.has(code) ? (code as ApiErrorCode) : null,
      issues,
      isPreconditionReason(envelope['reason']) ? envelope['reason'] : null,
      body,
    );
  },
};

/** The error a failed response's body (its text) stands for (`apiRefusals`). */
export function apiClientErrorFromBody(status: number, body: string): ApiClientError {
  return refusalError(status, body, apiRefusals) as ApiClientError;
}

/** The error of a failed response; reads its body. */
export async function apiClientErrorFromResponse(response: Response): Promise<ApiClientError> {
  return apiClientErrorFromBody(response.status, await response.text().catch(() => ''));
}

/**
 * A call whose answer is not the `ApiResult` envelope (a stream, a file, a plain JSON body,
 * another server of this machine): its `Response` once it succeeded; a refusal throws its
 * {@link ApiClientError}.
 */
export async function apiResponse(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return await okResponse(await fetch(input, init), apiRefusals);
}

/**
 * Client-side companion to the server's `apiSuccess`/`apiFailure`: performs a
 * `fetch`, unwraps the `ApiResult` envelope, and either returns `data` or throws
 * an {@link ApiClientError} carrying the failure's code and reason. Use for the
 * common case; `apiResponse` when a caller needs the raw response or streaming. Its errors are
 * read by the service kit (`apiRefusals`); it keeps `fetch`'s own arguments, for this machine's
 * other servers and the Desktop's origins.
 */
export async function apiFetch<TData>(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<TData> {
  const response = await fetch(input, init);
  const payload = await response.text();
  if (!response.ok) throw apiClientErrorFromBody(response.status, payload);
  let result: ApiResult<TData> | null = null;
  try {
    result = JSON.parse(payload) as ApiResult<TData>;
  } catch {
    // Preserve the HTTP status when an intermediary returns a non-JSON response.
  }
  if (result === null || record(result) === null)
    throw new ApiClientError(`Request failed (HTTP ${response.status}).`, response.status);
  if (!result.ok) throw apiClientErrorFromBody(response.status, payload);
  return result.data;
}
